// redux/slices/promoSlice.js
"use client";

import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

/* ========================= HELPERS: AUTH CONFIG ========================= */

const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

const authJsonConfigWithContentType = async () => {
  const baseCfg = await authJsonConfig();
  return {
    ...baseCfg,
    headers: {
      ...baseCfg.headers,
      "Content-Type": "application/json",
    },
  };
};

/* ========================= Normalizers ========================= */

const normalizePromo = (p = {}) => ({
  id: p.id ?? p.promoId ?? null,
  code: p.code ?? p.promoCode ?? "",
  discountType: p.discountType ?? p.discount_type ?? "", // 'percent' | 'amount'
  discountValue: Number(p.discountValue ?? p.discount_value ?? 0),
  usageLimit: p.usageLimit ?? p.usage_limit ?? null,
  usedCount: p.usedCount ?? p.used_count ?? 0,
  startsAt: p.startsAt ?? p.starts_at ?? null,
  endsAt: p.endsAt ?? p.ends_at ?? null,
  isActive: p.isActive ?? p.is_active ?? undefined,
});

/* ========================= Thunks ========================= */

// Получение всех промокодов (приватный)
export const fetchPromoCodes = createAsyncThunk(
  "promo/fetchPromoCodes",
  async (_, { rejectWithValue }) => {
    try {
      const cfg = await authJsonConfig();
      const res = await axios.get(`${getServerUrl()}/promo`, cfg);

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
            res.data?.error ||
            `Fetch promo codes failed (HTTP ${res.status})`
        );
      }

      const arr = Array.isArray(res?.data)
        ? res.data
        : res?.data?.promos || [];
      return arr.map(normalizePromo);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Fetch promo codes failed"
      );
    }
  }
);

// Создание нового промокода (приватный)
export const createPromoCode = createAsyncThunk(
  "promo/createPromoCode",
  async (promoData, { rejectWithValue }) => {
    try {
      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/promo/create`,
        promoData,
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
            res.data?.error ||
            `Create promo code failed (HTTP ${res.status})`
        );
      }

      // сервер может вернуть { promo: {...} } или {...}
      const raw = res?.data?.promo || res?.data;
      return normalizePromo(raw);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Create promo code failed"
      );
    }
  }
);

// Удаление промокода (приватный)
export const deletePromoCode = createAsyncThunk(
  "promo/deletePromoCode",
  async (id, { rejectWithValue }) => {
    try {
      if (!id && id !== 0) throw new Error("Promo id not provided");

      const cfg = await authJsonConfig();
      const res = await axios.delete(
        `${getServerUrl()}/promo/${id}`,
        cfg
      );

      if (res.status !== 200 && res.status !== 204) {
        throw new Error(
          res.data?.message ||
            res.data?.error ||
            `Delete promo code failed (HTTP ${res.status})`
        );
      }

      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Delete promo code failed"
      );
    }
  }
);

// Применение промокода (приватный)
export const applyPromoCode = createAsyncThunk(
  "promo/applyPromoCode",
  async (code, { rejectWithValue }) => {
    try {
      if (!code) throw new Error("Promo code not provided");

      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/promo/apply`,
        { code },
        cfg
      );

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
            res.data?.error ||
            `Apply promo code failed (HTTP ${res.status})`
        );
      }

      // ожидаем { code, discount_value, discount_type } и т.п.
      const d = res?.data || {};
      return {
        code: d.code ?? "",
        discountType: d.discount_type ?? d.discountType ?? "",
        discountValue: Number(d.discount_value ?? d.discountValue ?? 0),
        // необязательные поля
        message: d.message,
        promoId: d.promoId ?? d.id ?? null,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Apply promo code failed"
      );
    }
  }
);

/* ========================= Selectors ========================= */

export const selectPromoState = (s) => s.promo;
export const selectPromoCodes = (s) => s.promo?.promoCodes || [];
export const selectAppliedPromo = (s) => s.promo?.applied || null;
export const selectPromoStatus = (s) => s.promo?.status || "idle";
export const selectPromoError = (s) => s.promo?.error || null;

export const selectHasActivePromo = createSelector(
  [selectAppliedPromo],
  (ap) => !!(ap && ap.code)
);
export const selectDiscountValue = createSelector(
  [selectAppliedPromo],
  (ap) => ap?.discountValue ?? 0
);
export const selectDiscountType = createSelector(
  [selectAppliedPromo],
  (ap) => ap?.discountType ?? ""
);

export const selectPromoById = createSelector(
  [selectPromoCodes, (_, id) => id],
  (list, id) => list.find((p) => p.id === id) || null
);

/* ========================= Slice ========================= */

const initialState = {
  promoCodes: [],
  applied: null, // { code, discountType, discountValue, promoId?, message? }
  status: "idle", // idle | loading | succeeded | failed
  error: null,
};

const promoSlice = createSlice({
  name: "promo",
  initialState,
  reducers: {
    clearPromo: (state) => {
      state.applied = null;
      // статус/ошибку можно не трогать, чтобы UI не мерцал
    },
    upsertPromoLocal: (state, action) => {
      // полезно для оптимистичных апдейтов
      const incoming = normalizePromo(action.payload || {});
      if (!incoming?.id) return;
      const idx = state.promoCodes.findIndex((p) => p.id === incoming.id);
      if (idx === -1) state.promoCodes.push(incoming);
      else state.promoCodes[idx] = {
        ...state.promoCodes[idx],
        ...incoming,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- fetchPromoCodes ---------- */
      .addCase(fetchPromoCodes.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.promoCodes = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchPromoCodes failed";
      })

      /* ---------- createPromoCode ---------- */
      .addCase(createPromoCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createPromoCode.fulfilled, (state, action) => {
        state.status = "succeeded";
        const promo = action.payload;
        if (promo?.id != null) state.promoCodes.push(promo);
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "createPromoCode failed";
      })

      /* ---------- deletePromoCode ---------- */
      .addCase(deletePromoCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deletePromoCode.fulfilled, (state, action) => {
        state.status = "succeeded";
        const id = action.payload;
        state.promoCodes = state.promoCodes.filter(
          (p) => p.id !== id
        );
        // если удалили активный промо — сбрасываем applied
        if (state.applied?.promoId === id) state.applied = null;
      })
      .addCase(deletePromoCode.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "deletePromoCode failed";
      })

      /* ---------- applyPromoCode ---------- */
      .addCase(applyPromoCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(applyPromoCode.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.applied = action.payload || null;
      })
      .addCase(applyPromoCode.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "applyPromoCode failed";
        state.applied = null; // промо не применился
      })

      /* ---------- reset ---------- */
      .addCase(resetStore, () => initialState);
  },
});

export const { clearPromo, upsertPromoLocal } = promoSlice.actions;
export default promoSlice.reducer;
